import { replace } from 'redux-first-history'

import { formatRoute, MY_5_DYNAMICS } from 'constants/urls'
import { fromCallbacksToPromise, isDateExpired } from 'helpers'
import { EXPIRATION_STRATEGY } from 'app_modules/accounts/constants'
import { updateState } from 'app_modules/assessment/actions'
import { notifyWarning } from 'app_modules/notifications/actions'
import actionTypes from 'app_modules/session/constants'
import api from 'api'

import { fetchDashboard } from './fetchDashboard'
import { fetchVideos } from './fetchVideos'
import { logout } from './fetchLogout'
import { setCurrentProfile } from './setProfile'
import { setCurrentUser, setImpersonateUser } from './setUser'

// Matches the account id in private routes such as /account/42/my-5-dynamics
const ACCOUNT_ID_IN_PATH = /^\/account\/(\d+)(\/|$)/

export const RESTORE_OUTCOME = Object.freeze({
  RESTORED: 'restored',
  LOGGED_OUT: 'loggedOut',
  RETRYABLE: 'retryable',
})

// Actions

export const restoreSessionRequest = () => ({
  type: actionTypes.RESTORE_SESSION_REQUEST,
})

export const restoreSessionSuccess = () => ({
  type: actionTypes.RESTORE_SESSION_SUCCESS,
})

export const restoreSessionFailure = error => ({
  payload: error,
  type: actionTypes.RESTORE_SESSION_FAILURE,
})

// Helpers

const getAccountIdFromPath = () => {
  const match = ACCOUNT_ID_IN_PATH.exec(window.location.pathname)
  return match ? parseInt(match[1], 10) : null
}

// Same rule setCurrentProfileByAccountId uses to refuse switching to an expired account.
const isProfileExpired = profile =>
  Boolean(
    profile?.license &&
    isDateExpired(profile.license.expiresAt) &&
    !profile.limitedMember &&
    profile.account?.expirationStrategy !== EXPIRATION_STRATEGY.DO_NOTHING,
  )

// Whether the URL points at an account the user has a profile for, but cannot open because
// it is expired. The restored profile then belongs to another account, so the URL is redirected.
const isUrlAccountExpired = ({ impersonateToken, profiles }) => {
  const urlAccountId = getAccountIdFromPath()
  const urlProfile = urlAccountId && profiles.find(({ account }) => account?.id === urlAccountId)
  return Boolean(urlProfile && !impersonateToken && isProfileExpired(urlProfile))
}

/**
 * Picks the profile to make current after a reload: the one for the account in the URL,
 * so the user stays where they were, otherwise the same default setUser picks at login.
 *
 * Expired profiles are skipped (unless impersonating), like setCurrentProfileByAccountId and
 * setDefaultProfile do, so their endpoints are never called. When the default account is
 * expired, the first non-expired profile is used, as setDefaultProfile does. When there is
 * no profile for the default account, or every profile is expired, nothing is returned and
 * the caller logs the user out.
 */
const pickCurrentProfile = ({ defaultAccount, impersonateToken, profiles }) => {
  const urlAccountId = getAccountIdFromPath()
  const urlProfile = urlAccountId && profiles.find(({ account }) => account?.id === urlAccountId)

  if (impersonateToken) {
    return urlProfile || profiles.find(({ account }) => account?.isPersonal) || profiles[0]
  }

  if (urlProfile && !isProfileExpired(urlProfile)) {
    return urlProfile
  }

  const defaultProfile = profiles.find(({ account }) => account?.id === defaultAccount?.id)

  // No profile for the default account: setUser logs out in that case too.
  if (!defaultProfile || !isProfileExpired(defaultProfile)) {
    return defaultProfile
  }

  return profiles.find(profile => !isProfileExpired(profile))
}

/**
 * Whether a failed restore should be retried instead of logging the user out.
 *
 * Expired or invalid tokens never get here: ApiEndpoint handles them through expireSession.
 * What does get here is either a definitive API error (it carries an errorCode, e.g. account
 * expired or not found), which logs out, or a transient one, which keeps the token:
 * - isConnectionError: ApiEndpoint gave up after its retries (CAN_NOT_CONNECT)
 * - no errorCode: a server error without an API error body (5xx)
 */
const isRetryableError = ({ error, isConnectionError } = {}) => Boolean(isConnectionError || !error?.errorCode)

// Thunks

/**
 * Rebuilds the in-memory user state after a full page reload (SIM-388, pentest 6.7).
 *
 * Only the session token survives in localStorage; profiles, scores and account data do not.
 * This calls GET /sessions/current with the persisted token and dispatches the same actions
 * setUser dispatches at login, without redirecting. If the token is no longer valid the user
 * is logged out, exactly as an expired session is today.
 *
 * Resolves with the outcome, which SessionRestore uses to decide what to render:
 * - RESTORE_OUTCOME.RESTORED: the state is rebuilt
 * - RESTORE_OUTCOME.LOGGED_OUT: the session cannot be used; logout() already ran
 * - RESTORE_OUTCOME.RETRYABLE: a transient failure (connection, server error, rate limit);
 *   the token is kept so the user can try again without logging in
 */
export const restoreSession = () => {
  const thunk = (dispatch, getState) => {
    // Keep the token the FE already holds: /sessions/current does not return the regular token,
    // and SET_CURRENT_USER would otherwise reset it. SSO and impersonation tokens are returned.
    const { token } = getState().user

    dispatch(restoreSessionRequest())

    const loggedOut = error => {
      dispatch(restoreSessionFailure(error))
      return Promise.resolve(dispatch(logout())).then(() => RESTORE_OUTCOME.LOGGED_OUT)
    }

    const handleResponse = response => {
      const { user } = response || {}

      // ApiEndpoint answers a rate-limited request through the success callback with an
      // empty body. Nothing to rebuild from, but the session itself is fine.
      if (!user) {
        dispatch(restoreSessionFailure(null))
        return RESTORE_OUTCOME.RETRYABLE
      }

      const { hasActiveAssessment: isCompleted, hasOngoingAssessment: isInProgress, impersonateToken } = user
      const profiles = user.profiles || []
      const currentProfile = pickCurrentProfile({ ...user, profiles })

      if (!currentProfile) {
        return loggedOut(null)
      }

      const userAction = impersonateToken ? setImpersonateUser : setCurrentUser

      dispatch(updateState({ isCompleted, isInProgress }))
      dispatch(userAction({ ...user, profiles, token }))
      dispatch(setCurrentProfile(currentProfile))

      // The URL account is expired, so another profile was restored. Move the URL to that
      // account before the routes mount, otherwise the page would render one account under
      // another account's URL. Same warning setCurrentProfileByAccountId shows for this case.
      if (isUrlAccountExpired({ impersonateToken, profiles })) {
        dispatch(replace(formatRoute(MY_5_DYNAMICS, { accountId: currentProfile.account.id })))
        dispatch(notifyWarning({ label: 'accountExpired' }))
      }

      dispatch(restoreSessionSuccess())

      // Same follow-up fetches setUser runs at login. Not awaited: pages render while they load.
      // Their errors are already notified inside each thunk:
      // - fetchVideos returns a subscription, not a promise, and rethrows inside it unless an
      //   onError callback is given, so a no-op one is passed.
      // - fetchDashboard returns a promise that rethrows, so it is caught here.
      dispatch(fetchVideos(undefined, undefined, () => {}))
      Promise.resolve(dispatch(fetchDashboard())).catch(() => {})

      return RESTORE_OUTCOME.RESTORED
    }

    const handleError = (failure = {}) => {
      if (isRetryableError(failure)) {
        dispatch(restoreSessionFailure(failure.error))
        return RESTORE_OUTCOME.RETRYABLE
      }
      return loggedOut(failure.error)
    }

    // An unexpected exception while rebuilding the state fails closed: log out rather than
    // render the app with a half-built store.
    return fromCallbacksToPromise(api.session.current)
      .then(handleResponse, handleError)
      .catch(() => loggedOut(null))
  }

  thunk.type = actionTypes.RESTORE_SESSION

  return thunk
}

export default restoreSession
